import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { UserAvatar } from './UserAvatar'
import { useAcceptFriendRequest, useDeclineFriendRequest } from '../hooks/useFriends'
import type { PendingRequest } from '../types'

interface FriendRequestListProps {
  readonly requests: PendingRequest[]
}

/** Invitations reçues, en attente de réponse. */
export function FriendRequestList({ requests }: FriendRequestListProps) {
  const { t, i18n } = useTranslation()
  const accept = useAcceptFriendRequest()
  const decline = useDeclineFriendRequest()

  if (requests.length === 0) {
    return null
  }

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(new Date(iso)) : ''

  return (
    <section aria-labelledby="social-requests-title" className="flex flex-col gap-4">
      <h2
        id="social-requests-title"
        className="font-display text-lg font-semibold text-on-surface"
      >
        {t('social.requests.title', { count: requests.length })}
      </h2>

      <ul className="flex flex-col gap-2">
        {requests.map((request) => (
          <li
            key={request.id}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-outline-variant bg-surface px-4 py-3"
          >
            <UserAvatar
              username={request.requester.username}
              avatarUrl={request.requester.avatar_url}
            />

            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold text-on-surface">
                {request.requester.username}
              </span>
              <span className="text-xs text-on-surface-variant">
                {formatDate(request.created_at)}
              </span>
            </span>

            <span className="flex gap-2">
              <Button
                size="sm"
                onClick={() => accept.mutate(request.id)}
                isLoading={accept.isPending && accept.variables === request.id}
              >
                {t('social.requests.accept')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => decline.mutate(request.id)}
                isLoading={decline.isPending && decline.variables === request.id}
              >
                {t('social.requests.decline')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
